import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Text,
  Textarea,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useCallback, useMemo, useState } from 'react'
import { useEnqueueSettlementVerification, useSettlementRecords } from '@/hooks/useAdminData'
import { useToast } from '@/hooks/useToast'
import { SettlementRecord, SettlementVerificationEnqueueResult } from '@/types'
import { formatChainName } from '@/constants/chains'
import { findEntriesWithoutPrefix } from '@/components/common/triggerInputHelpers'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { parseTextareaEntries } from './paymentManagementInputHelpers'
import { countSettlementVerificationResults } from './settlementTriggerResultCounts'

const coalesceRecordValue = (
  record: SettlementRecord,
  keys: readonly string[],
): unknown => {
  const source = record as unknown as Record<string, unknown>
  for (const key of keys) {
    if (!key) {
      continue
    }
    const value = source[key]
    if (value !== undefined && value !== null) {
      return value
    }
  }
  return undefined
}

const getRecordString = (
  record: SettlementRecord,
  keys: readonly string[],
): string | undefined => {
  const value = coalesceRecordValue(record, keys)
  if (value === undefined || value === null) {
    return undefined
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : undefined
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  if (value instanceof Date) {
    return value.toISOString()
  }
  return undefined
}

export function SettlementVerificationTrigger() {
  const { t } = useTranslation()
  const toast = useToast()

  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const subtitleColor = useColorModeValue('gray.600', 'gray.300')
  const resultsCardBg = useColorModeValue('gray.50', 'whiteAlpha.100')

  const [idsInput, setIdsInput] = useState('')
  const [limit, setLimit] = useState(10)
  const [chainFilter, setChainFilter] = useState('')
  const [results, setResults] = useState<{
    total: number
    queued: number
    failed: number
    skipped: number
    items: SettlementVerificationEnqueueResult[]
  } | null>(null)

  const { data, isLoading } = useSettlementRecords({ status: 'srs_pending', limit })
  const enqueueVerification = useEnqueueSettlementVerification()

  const guard = data?.guard
  const records = data?.data ?? []

  const chainOptions = useMemo(() => {
    const unique = new Set<string>()
    records.forEach(record => {
      const chainId = getRecordString(record, ['chainId', 'chain_id', 'chainCode', 'chain_code'])
      if (chainId) {
        unique.add(chainId)
      }
    })
    return Array.from(unique)
      .map(value => ({
        value,
        label: (() => {
          const formatted = formatChainName(value, 'short')
          return formatted && formatted.toLowerCase() !== 'unknown' ? formatted : value
        })(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [records])

  const resetResults = useCallback(() => setResults(null), [])

  const handleSubmit = useCallback(async () => {
    const entries = parseTextareaEntries(idsInput)
    const invalidEntries = findEntriesWithoutPrefix(entries, 'ord_')

    resetResults()

    if (invalidEntries.length > 0) {
      toast.warning({
        title: t('settlements.verification.warningNoSelection'),
        description: invalidEntries.join(', '),
      })
      return
    }

    const orderIds = entries

    if (!guard?.verificationQueueConfigured) {
      toast.warning({
        title: t('settlements.verification.queueMissingTitle'),
        description: t('settlements.verification.queueMissingDescription'),
      })
      return
    }

    if (!guard.verificationManualAllowed) {
      toast.info({
        title: t('settlements.verification.manualDisabledTitle'),
        description: t('settlements.verification.manualDisabledDescription', { mode: guard.mode }),
      })
      return
    }

    try {
      const payload: {
        orderIds?: string[]
        chainId?: string
        limit?: number
      } = {
        chainId: chainFilter || undefined,
        limit,
      }

      if (orderIds.length > 0) {
        payload.orderIds = orderIds
      }

      const response = await enqueueVerification.mutateAsync(payload)
      const { queued, failed, skipped } = countSettlementVerificationResults(response.results)

      if (response.results.length === 0 || response.message === 'no_pending_records') {
        toast.info({ title: t('settlements.verification.noPending') })
        setResults(null)
        return
      }

      if (queued > 0) {
        toast.success({ title: t('settlements.verification.success', { count: queued }) })
      }

      if (failed > 0) {
        toast.warning({
          title: t('settlements.verification.partialFailure', { count: failed }),
          description: response.results
            .filter(result => !result.queued)
            .map(result => `${result.orderId ?? result.settlementRecordId}: ${result.reason ?? 'unknown'}`)
            .join('\n'),
          duration: 8000,
        })
      }

      setResults({
        total: response.results.length,
        queued,
        failed,
        skipped,
        items: response.results,
      })

      setIdsInput('')
    } catch (error: any) {
      resetResults()
      toast.error({
        title: t('settlements.verification.errorTitle'),
        description: error?.message || t('settlements.verification.errorDescription'),
      })
    }
  }, [
    enqueueVerification,
    guard,
    idsInput,
    chainFilter,
    limit,
    resetResults,
    t,
    toast,
  ])

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={6} shadow="sm" bg={cardBg}>
      <VStack align="stretch" spacing={5}>
        {guard && !guard.verificationQueueConfigured && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>{t('settlements.verification.queueMissingTitle')}</AlertTitle>
              <AlertDescription>{t('settlements.verification.queueMissingDescription')}</AlertDescription>
            </Box>
          </Alert>
        )}

        {guard && guard.verificationQueueConfigured && !guard.verificationManualAllowed && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>{t('settlements.verification.manualDisabledTitle')}</AlertTitle>
              <AlertDescription>
                {t('settlements.verification.manualDisabledDescription', { mode: guard.mode })}
              </AlertDescription>
            </Box>
          </Alert>
        )}

        <FormControl>
          <FormLabel>{t('settlements.verification.fields.orderIds')}</FormLabel>
          <Textarea
            value={idsInput}
            onChange={(event) => setIdsInput(event.target.value)}
            placeholder={t('settlements.verification.fields.orderPlaceholder')}
            rows={5}
            fontFamily="mono"
          />
          <FormHelperText>{t('settlements.verification.fields.orderHelper')}</FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel>{t('settlements.verification.fields.chain')}</FormLabel>
          <DropdownSelect
            placeholder={t('settlements.verification.fields.chainPlaceholder') ?? ''}
            emptyOptionLabel={t('settlements.verification.fields.chainPlaceholder') ?? ''}
            value={chainFilter}
            options={chainOptions}
            onChange={value => {
              setChainFilter(value)
              resetResults()
            }}
            ariaLabel={t('settlements.verification.fields.chain')}
          />
          <FormHelperText>{t('settlements.verification.fields.chainHelper')}</FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel>{t('settlements.verification.fields.limit')}</FormLabel>
          <DropdownSelect
            value={String(limit)}
            options={[5, 10, 20, 50, 100].map(value => ({
              value: String(value),
              label: String(value),
            }))}
            onChange={value => {
              setLimit(Number(value))
              resetResults()
            }}
            ariaLabel={t('settlements.verification.fields.limit')}
          />
          <FormHelperText>{t('settlements.verification.fields.limitHelper')}</FormHelperText>
        </FormControl>

        <Button
          colorScheme="blue"
          onClick={handleSubmit}
          isLoading={enqueueVerification.isPending || isLoading}
          loadingText={t('settlements.verification.button.loading')}
        >
          {t('settlements.verification.button.submit')}
        </Button>

        {results && (
          <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={resultsCardBg} p={4}>
            <Text fontWeight="semibold" mb={3}>
              {t('settlements.verification.results.title')}
            </Text>
            <Box display="grid" gridTemplateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={3} mb={3}>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color={subtitleColor}>
                  {t('settlements.verification.results.total')}
                </Text>
                <Text fontSize="lg">{results.total}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color={subtitleColor}>
                  {t('settlements.verification.results.queued')}
                </Text>
                <Text fontSize="lg" color="green.400">{results.queued}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color={subtitleColor}>
                  {t('settlements.verification.results.failed')}
                </Text>
                <Text fontSize="lg" color="red.400">{results.failed}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color={subtitleColor}>
                  {t('settlements.verification.results.skipped')}
                </Text>
                <Text fontSize="lg" color="yellow.400">{results.skipped}</Text>
              </Box>
            </Box>

            <VStack align="stretch" spacing={1}>
              {results.items.slice(0, 10).map(item => (
                <Text key={item.settlementRecordId} fontFamily="mono" fontSize="sm">
                  {(item.orderId ?? item.settlementRecordId)} · {item.reason ?? (item.queued ? t('settlements.verification.results.queuedStatus') : t('settlements.verification.results.enqueued'))}
                </Text>
              ))}
              {results.items.length > 10 && (
                <Text fontSize="xs" color={subtitleColor}>
                  {t('settlements.verification.results.more', { count: results.items.length - 10 })}
                </Text>
              )}
            </VStack>
          </Box>
        )}
      </VStack>
    </Box>
  )
}
